import * as React from "react";
import {ArcSeries, FlexibleWidthXYPlot} from "react-vis";
import {Paper, Typography, ListItem, ListItemIcon, ListItemText, Skeleton, Grid, Avatar} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {
    Whatshot
} from "@mui/icons-material";
import {CoineusCryptoFormat} from "../utils/currency_format";
import VoltageLogo from "../images/voltage_finance.svg";

const useStyles = makeStyles((theme) => ({
    xyplot: {
        margin: '0 auto',
    },
}));

function BurnSupply(props) {
    const classes = useStyles();

    const {minted, supply, liquidity, liquidityV4, voltLP, litLP, ceusone, staked} = props;

    const divider = 360 * .0075 * (Math.PI / 180);
    const aBurned = 360 * ((minted - supply) / minted) * (Math.PI / 180);

    const aLiquidity = 360 * (liquidity / supply) * (Math.PI / 180);

    const aLiquidityV4 = 360 * (liquidityV4 / supply) * (Math.PI / 180);

    const aLitLP = 360 * (litLP / supply) * (Math.PI / 180);
    const aVoltLP = 360 * (voltLP / supply) * (Math.PI / 180);
    const aCeusone = 360 * (ceusone / supply) * (Math.PI / 180);
    const aStaked = 360 * (staked / supply) * (Math.PI / 180);

    const angles = {

        burnedStart: 0,
        burnedEnd: aBurned,

        supplyStart: aBurned + divider,
        supplyEnd: 360 * (Math.PI / 180) - divider,

        liquidityStart: aBurned + divider,
        liquidityEnd: aBurned + aLiquidity,

        liquidityV4Start: aBurned + aLiquidity + divider,
        liquidityV4End: aBurned + aLiquidity + aLiquidityV4,

        voltLPStart: aBurned + aLiquidity + aLiquidityV4 + divider,
        voltLPEnd: aBurned + aLiquidity + aLiquidityV4 + aVoltLP,

        litLPStart: aBurned + aLiquidity + aLiquidityV4 + aVoltLP + divider,
        litLPEnd: aBurned + aLiquidity + aLiquidityV4 + aVoltLP + aLitLP,

        ceusoneStart: aBurned + aLiquidity + aLiquidityV4 + aVoltLP + aLitLP + divider,
        ceusoneEnd: aBurned + aLiquidity + aLiquidityV4 + aVoltLP + aLitLP + aCeusone,

        stakedStart: aBurned + aLiquidity + aLiquidityV4 + aVoltLP + aLitLP + aCeusone + divider,
        stakedEnd: aBurned + aLiquidity + aLiquidityV4 + aVoltLP + aLitLP + aCeusone + aStaked,
    }

    if (
        Number.isNaN(angles.liquidityEnd) ||
        Number.isNaN(angles.litLPStart) ||
        Number.isNaN(angles.litLPEnd) ||
        Number.isNaN(angles.ceusoneStart) ||
        Number.isNaN(angles.ceusoneEnd) ||
        Number.isNaN(angles.stakedStart) ||
        Number.isNaN(angles.stakedEnd)
    ) {
        return <></>;
    }


    const colors = {
        supply: '#ccc',
        burned: '#fa5700',
        liquidity: '#76EC00',
        liquidityV4: '#76EC00',
        voltLP: '#76EC00',
        litlp: '#fff',
        ceusone: '#fbca00',
        staked: '#fbca00',
    }

    if (
        supply === undefined ||
        liquidity === undefined
    ) {
        return <Paper style={{padding: 20}}>
            <Skeleton variant="circular" height={180} width={180} style={{margin: '0 auto'}}/>
        </Paper>;
    }

    return (
        <Paper style={{
            padding: 20
        }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FlexibleWidthXYPlot
                        xDomain={[-5, 5]}
                        yDomain={[-5, 5]}
                        height={200}
                        margin={{left: -20, right: -20, top: -20, bottom: -20}}
                        className={classes.xyplot}
                    >
                        <ArcSeries
                            radiusDomain={[0, 10]}
                            center={{x: 0, y: 0}}
                            data={[
                                {
                                    angle0: angles.supplyStart,
                                    angle: angles.supplyEnd,
                                    opacity: 1,
                                    radius: 6.75,
                                    radius0: 6,
                                    color: colors.supply,
                                },
                                {
                                    angle0: angles.burnedStart,
                                    angle: angles.burnedEnd,
                                    opacity: 1,
                                    radius: 6.75,
                                    radius0: 6,
                                    color: colors.burned,
                                },
                                {
                                    angle0: angles.liquidityStart,
                                    angle: angles.liquidityEnd,
                                    opacity: 1,
                                    radius: 7.5,
                                    radius0: 7.25,
                                    color: colors.liquidity,
                                },
                                {
                                    angle0: angles.liquidityV4Start,
                                    angle: angles.liquidityV4End,
                                    opacity: 1,
                                    radius: 7.5,
                                    radius0: 7.25,
                                    color: colors.liquidityV4,
                                },
                                {
                                    angle0: angles.voltLPStart,
                                    angle: angles.voltLPEnd,
                                    opacity: 1,
                                    radius: 7.5,
                                    radius0: 7.25,
                                    color: colors.voltLP,
                                },
                                {
                                    angle0: angles.litLPStart,
                                    angle: angles.litLPEnd,
                                    opacity: 1,
                                    radius: 7.5,
                                    radius0: 7.25,
                                    color: colors.litlp,
                                },
                                {
                                    angle0: angles.ceusoneStart,
                                    angle: angles.ceusoneEnd,
                                    opacity: 1,
                                    radius: 7.5,
                                    radius0: 7.25,
                                    color: colors.ceusone,
                                },
                                {
                                    angle0: angles.stakedStart,
                                    angle: angles.stakedEnd,
                                    opacity: 1,
                                    radius: 7.5,
                                    radius0: 7.25,
                                    color: colors.staked,
                                }
                            ]}
                            colorType={'literal'}
                        />
                    </FlexibleWidthXYPlot>

                    <Typography
                        style={{
                            height: 0,
                            width: '100%',
                            textAlign: 'center',
                            top: -145,
                            position: 'relative',
                            fontSize: 20,
                            fontWeight: '700'
                        }}
                    >
                        {(((minted - supply) / minted) * 100).toFixed(2)}
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: '400',
                                marginTop: -5,
                                display: 'block'
                            }}
                        >
          % BURNED
        </span>
                    </Typography>

                    <Typography
                        style={{
                            height: 0,
                            width: '100%',
                            textAlign: 'center',
                            top: -105,
                            position: 'relative',
                            fontSize: 20,
                            fontWeight: '700',
                            color: colors.staked
                        }}
                    >
                        {((staked / supply) * 100).toFixed(2)}
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: '400',
                                marginTop: -5,
                                display: 'block',
                            }}
                        >
          % STAKED
        </span>
                    </Typography>
                </Grid>
                <Grid item xs={12}>

                    <Grid container>

                        <Grid item xs={6}>
                            <StatListItem icon={<Avatar
                                src={`https://coineus.app/assets/tokens/fuse/0x4e69Ae0CD024754655b4eF74F24A8DCB39Ba07e8/logo.png`}
                                sx={{width: 24, height: 24}}/>} color={colors.supply}
                                          value={supply}
                                          label="SUPPLY"/>
                        </Grid>
                        <Grid item xs={6}>
                            <StatListItem icon={<Avatar
                                src={VoltageLogo} sx={{width: 24, height: 24}}/>}
                                          color={colors.liquidity} value={liquidity}
                                          label="VOLTAGE V2 LP"/>
                        </Grid>
                        <Grid item xs={6}>
                            <StatListItem icon={<Whatshot style={{color: colors.burned}}/>} color={colors.burned}
                                          value={minted - supply}
                                          label="BURNED"/>
                        </Grid>
                        <Grid item xs={6}>
                            <StatListItem icon={<Avatar
                                src={VoltageLogo} sx={{width: 24, height: 24}}/>}
                                          color={colors.liquidityV4} value={liquidityV4}
                                          label="VOLTAGE V4 LP"/>
                        </Grid>
                        <Grid item xs={6}>
                            <StatListItem icon={<Avatar
                                src={`https://coineus.app/assets/tokens/fuse/0xF2C6C1AA2bf8ec40F564Ea8A483F64907ea37A3F/logo.png`}
                                sx={{width: 24, height: 24}}/>} color={colors.litlp} value={litLP}
                                          label="LIT LP"/>
                        </Grid>
                        <Grid item xs={6}>
                            <StatListItem icon={<Avatar
                                src={VoltageLogo} sx={{width: 24, height: 24}}/>}
                                          color={colors.voltLP} value={voltLP}
                                          label="VOLT LP"/>
                        </Grid>
                        <Grid item xs={6}>
                            <StatListItem icon={<Avatar
                                src={`https://coineus.app/assets/tokens/fuse/0xE27431FACb1F093BA164c9F86B3f484b39ad54CC/logo.png`}
                                sx={{width: 24, height: 24}}/>} color={colors.ceusone} value={ceusone}
                                          label="CEUSONE"/>
                        </Grid>
                        <Grid item xs={6}>
                            <StatListItem icon={<Avatar
                                src={`https://coineus.app/assets/tokens/fuse/0x4e69Ae0CD024754655b4eF74F24A8DCB39Ba07e8/logo.png`}
                                sx={{width: 24, height: 24}}/>} color={colors.staked} value={staked}
                                          label="CEUS STAKED"/>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default BurnSupply;

function StatListItem(props) {
    const {color, value, label, icon} = props;

    return <ListItem disablePadding>
        <ListItemIcon style={{paddingLeft: 15}}>
            {icon}
        </ListItemIcon>
        <ListItemText
            primary={CoineusCryptoFormat(value)}
            secondary={label}
            primaryTypographyProps={{fontSize: '.75rem',fontWeight: 700, style: {color}}}
            secondaryTypographyProps={{fontSize: '.65rem', style: {color}}}
        />
    </ListItem>;
}
